<script setup lang="ts">
import { ref, onActivated, onMounted, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useInfiniteScroll } from '@vueuse/core'
import { Search } from 'lucide-vue-next'
import Waterfall from '@/components/grid-fall.vue'
import { apiGetTheaterCats, apiGetTheaterByPayload } from '../../apis/theater'
import type { IReqTheaterCat, IResTheaterItem } from '@/types/apis/theater'
import DataFeedback from '@/components/data-feedback.vue'
import { useResponsiveColumns } from '@/hooks/useResponsiveColumns'
import { Icon } from '@iconify/vue'
import { useUserStore } from '@/stores/user'

// 使用响应式列数 hook
const { columns } = useResponsiveColumns()
const { t } = useI18n()
const userStore = useUserStore()
const consumedAuthVersion = ref(userStore.authVersion)

// 定义每个分类的数据状态
interface CategoryData {
  list: IResTheaterItem[]
  loading: boolean
  currentPage: number
  hasMore: boolean
  loaded: boolean // 标记是否已加载过
}

const categories = ref<IReqTheaterCat[]>([
  {
    CategoryType: 'ScriptType',
    Icon: null,
    Id: 0,
    Name: t('component.theater.allCategories'),
    NameEn: 'All',
  },
])
const active = ref(0)

// 为每个分类维护独立的数据状态
const categoryDataMap = ref<Map<number, CategoryData>>(new Map())

// 获取或初始化分类数据
const getCategoryData = (categoryId: number): CategoryData => {
  if (!categoryDataMap.value.has(categoryId)) {
    categoryDataMap.value.set(categoryId, {
      list: [],
      loading: false,
      currentPage: 1,
      hasMore: true,
      loaded: false,
    })
  }
  return categoryDataMap.value.get(categoryId)!
}

const resetCategoryData = (categoryData: CategoryData) => {
  categoryData.list = []
  categoryData.loading = false
  categoryData.currentPage = 1
  categoryData.hasMore = true
  categoryData.loaded = false
}

const applyCategories = (nextCategories: IReqTheaterCat[]) => {
  categories.value = [
    {
      CategoryType: 'ScriptType',
      Icon: null,
      Id: 0,
      Name: t('component.theater.allCategories'),
      NameEn: 'All',
    },
    ...nextCategories,
  ]

  const newMap = new Map<number, CategoryData>()
  categories.value.forEach(v => {
    newMap.set(v.Id, {
      list: [],
      loading: false,
      currentPage: 1,
      hasMore: true,
      loaded: false,
    })
  })
  categoryDataMap.value = newMap

  if (!categories.value.some(category => category.Id === active.value)) {
    active.value = categories.value[0]?.Id ?? 0
  }
}

const loadCategories = async () => {
  const response = await apiGetTheaterCats()
  if (!response.IsSuccess || !response.Body) return false

  const sortedCategories = [...response.Body].sort((a, b) => a.Id - b.Id)
  applyCategories(sortedCategories)
  return true
}

// 当前激活的分类
const currentCategory = computed(() => categories.value[active.value])

// 当前分类的数据
const currentCategoryData = computed(() => {
  if (!currentCategory.value) return null
  return getCategoryData(currentCategory.value.Id)
})

// 获取剧本列表
const fetchTheaters = async (categoryId: number, isLoadMore = false) => {
  const categoryData = getCategoryData(categoryId)

  if (categoryData.loading) return

  try {
    categoryData.loading = true
    const response = await apiGetTheaterByPayload({
      CategoryId: categoryId,
      CategoryType: 'ScriptType',
      Keyword: '',
      PageIndex: categoryData.currentPage,
      PageSize: 20,
    })

    if (response.IsSuccess && response.Body) {
      const items = response.Body.Items || []
      if (isLoadMore) {
        categoryData.list = [...categoryData.list, ...items]
      } else {
        categoryData.list = items
      }
      console.log(response.Body.HasNextPage, 'hasMore?')
      categoryData.hasMore = response.Body.HasNextPage
      categoryData.loaded = true
    }
  } catch (error) {
    console.error('获取剧本列表失败:', error)
  } finally {
    categoryData.loading = false
  }
}

// 加载更多
const loadMore = () => {
  if (!currentCategory.value || !currentCategoryData.value) return

  const categoryData = currentCategoryData.value
  // 只有在已有数据且不在加载中且还有更多数据时才加载
  if (!categoryData.loading && categoryData.hasMore && categoryData.list.length > 0) {
    categoryData.currentPage++
    fetchTheaters(currentCategory.value.Id, true)
  }
}

// 配置触底加载
const refContainer = ref<HTMLElement | null>(null)
useInfiniteScroll(
  refContainer,
  () => {
    loadMore()
  },
  { distance: 150 },
)

// 监听 tab 切换

watch(active, newIndex => {
  const category = getCatInfo(newIndex)
  if (category) {
    const categoryData = getCategoryData(category.Id)
    // 如果该分类还没有加载过数据，则加载
    if (!categoryData.loaded) {
      fetchTheaters(category.Id, false)
    }
  }
})

// 初始化数据
onMounted(async () => {
  try {
    const loaded = await loadCategories()
    if (loaded) {
      const activeCategory = getCatInfo(active.value) || categories.value[0]
      if (activeCategory) {
        fetchTheaters(activeCategory.Id, false)
      }
    }
  } catch (error) {
    console.error('获取分类失败:', error)
  }
})

onActivated(async () => {
  if (consumedAuthVersion.value === userStore.authVersion) return

  consumedAuthVersion.value = userStore.authVersion

  try {
    const loaded = await loadCategories()
    if (!loaded) {
      categoryDataMap.value.forEach(categoryData => {
        resetCategoryData(categoryData)
      })
    }
  } catch (error) {
    console.error('登录后刷新分类失败:', error)
    categoryDataMap.value.forEach(categoryData => {
      resetCategoryData(categoryData)
    })
  }

  const activeCategory = getCatInfo(active.value) || categories.value[0]
  if (activeCategory) {
    void fetchTheaters(activeCategory.Id, false)
  }
})

const getCatInfo = (catId: number) => {
  return categories.value.find(cat => cat.Id === catId)
}
</script>

<template>
  <div class="page-content h-screen overflow-y-auto bg-gray-950 pb-20" ref="refContainer">
    <!-- 搜索栏 -->
    <div class="py-4 sticky top-0 bg-gray-950 z-10 flex flex-col gap-4">
      <div
        class="mx-4 relative bg-gray-900 h-10 flex gap-4 text-sm px-4 text-gray-200 items-center rounded-xl"
        @click="$router.push('/theater/search')"
      >
        <Search :size="18" />
        <span>{{ t('component.theater.search') }}</span>
      </div>
    </div>

    <van-tabs
      v-model:active="active"
      swipeable
      :sticky="true"
      :offset-top="72"
      style="
        --van-tabs-bottom-bar-height: 0;
        --van-tabs-bottom-bar-color: var(--color-purple-600);
        --van-tabs-nav-background: var(--color-gray-950);
        --van-tab-active-text-color: var(--color-purple-500);
      "
    >
      <van-tab
        v-for="[catId, catData] in categoryDataMap.entries()"
        :name="catId"
        :title="getCatInfo(catId)?.Name"
        :key="catId"
      >
        <template #title>
          <div
            class="px-3 py-2 -mx-2 rounded-lg text-xs bg-gray-800 text-gray-300 font-normal!"
            :class="[
              active === catId ? 'bg-purple-600/20 text-purple-500' : 'bg-gray-800 text-gray-300 hover:bg-gray-700',
            ]"
          >
            {{ getCatInfo(catId)?.Name }}
          </div>
        </template>
        <div class="p-4">
          <Waterfall :items="catData.list" :columns="columns" :gap="16">
            <template #default="{ item }">
              <div
                class="bg-gray-900 rounded-xl overflow-hidden shadow-lg cursor-pointer transition-transform duration-200"
                @click="$router.push(`/theater/detail/${item.IdKey}`)"
              >
                <!-- 视频封面 -->
                <div class="relative overflow-hidden bg-gray-800">
                  <img
                    v-if="item.CoverImageUrl"
                    :src="item.CoverImageUrl"
                    :alt="item.Name"
                    class="hover:scale-105 w-full h-auto object-cover transition-all duration-300 hover:opacity-90"
                  />
                  <div v-else class="aspect-3/4 flex items-center justify-center text-gray-600">
                    <Icon icon="solar:video-library-bold-duotone" class="text-5xl" />
                  </div>
                  <!-- 集数标签 -->
                  <div
                    v-if="item.TotalEpisodes"
                    class="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-lg"
                  >
                    {{ t('component.theater.episodeLabel', { count: item.TotalEpisodes }) }}
                  </div>
                </div>
                <!-- 视频信息 -->
                <div class="p-3">
                  <h3 class="text-white text-sm line-clamp-2 mb-2 break-all">
                    {{ item.Name || t('component.theater.untitled') }}
                  </h3>
                  <div
                    v-if="item.CategoryTags && item.CategoryTags.length > 0"
                    class="flex flex-wrap gap-x-1 gap-y-2 mb-2"
                  >
                    <span
                      v-for="(tag, index) in item.CategoryTags.slice(0, 2)"
                      :key="index"
                      class="bg-gray-800 text-gray-300 text-xs px-2 py-0.5 rounded-md"
                    >
                      {{ tag.Name }}
                    </span>
                  </div>
                </div>
              </div>
            </template>
          </Waterfall>

          <!-- 加载状态 -->
          <DataFeedback
            :empty="catData.list.length === 0"
            :loading="catData.loading"
            :finished="!catData.hasMore && catData.list.length > 0"
            :empty-text="t('component.theater.empty')"
          />
        </div>
      </van-tab>
    </van-tabs>
  </div>
</template>

<style scoped>
/* 隐藏滚动条 */
::-webkit-scrollbar {
  display: none;
}
</style>
